import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { formatDuration } from '@Utils/formatDuration';
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ReplayIcon from '@mui/icons-material/Replay';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import { Button, CircularProgress, IconButton, Modal, Tooltip } from '@mui/material';
import { rgba } from 'polished';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * THE student unit-progress popup — every one in both portals uses it: the
 * student Courses tab, the student Subscriptions tab, and the course Users
 * roster (classroom courses, and standalone lectures, whose roster runs the
 * same `progress/course/*` calls through the lecture-scope request rewrite).
 * The instructor app imports this file from the admin app.
 */

type UnitRow = {
	id: number;
	name: string;
	type_id: number;
	status: string | null;
	time: string | number | null;
	allowed_trials: number | null;
	attempts_used: number;
	max_plays: number | null;
	plays: number;
};

type Header = { course?: { id: number; name: string; parent: string | null } };

type Student = { id: number; first_name?: string; last_name?: string; username?: string };

type Action = 'complete' | 'reset';

/** unit_types ids → icon, label, and what a reset deletes for that type. */
const UNIT_TYPES: Record<number, { label: string; Icon: React.ElementType; resets: string }> = {
	1: { label: 'Document', Icon: DescriptionOutlinedIcon, resets: 'clears its progress and learning time' },
	2: { label: 'Video', Icon: PlayCircleFilledIcon, resets: 'clears its views and learning time' },
	3: { label: 'Test', Icon: LiveHelpOutlinedIcon, resets: "deletes the student's attempts on this test" },
	4: { label: 'Web content', Icon: DocumentScannerOutlinedIcon, resets: 'clears its progress and learning time' },
	5: { label: 'Webinar', Icon: PodcastsIcon, resets: "removes the student's webinar booking" },
	6: { label: 'Assignment', Icon: BallotOutlinedIcon, resets: "deletes the student's submissions" },
};

const STATUS: Record<string, { label: string; icon: JSX.Element }> = {
	completed: { label: 'Completed', icon: <CheckCircleIcon color="primary" /> },
	failed: { label: 'Failed', icon: <CancelIcon sx={{ color: '#ff3100' }} /> },
	review: { label: 'Awaiting review', icon: <HourglassTopIcon sx={{ color: '#ffab00' }} /> },
	'in progress': { label: 'In progress', icon: <TimelapseIcon sx={{ color: '#2196f3' }} /> },
};
const NOT_STARTED = { label: 'Not started', icon: <RadioButtonUncheckedIcon sx={{ color: '#bdbdbd' }} /> };

/** Tests: attempts used / allowed. Videos: views / limit (count alone when unlimited). */
const usage = (unit: UnitRow) => {
	if (Number(unit.type_id) === 3 && unit.allowed_trials !== undefined) {
		const allowed = Number(unit.allowed_trials) || 0;
		return allowed ? `${unit.attempts_used} / ${allowed} attempts` : `${unit.attempts_used} attempts`;
	}
	if (Number(unit.type_id) === 2) {
		const max = Number(unit.max_plays) || 0;
		if (max) return `${unit.plays} / ${max} views`;
		return Number(unit.plays) ? `${unit.plays} views` : '';
	}
	return '';
};

/** completed → reset; failed → reset or complete (pass, keep attempts); else → complete. */
const actionsFor = (status: string | null): Action[] =>
	status === 'completed' ? ['reset'] : status === 'failed' ? ['reset', 'complete'] : ['complete'];

const apiData = <T,>(response: any): T => response.data.data as T;
const errorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message || error?.data?.message || fallback;

type Props = {
	student: Student;
	/** course id, or the standalone lecture id */
	courseId: number;
	/** the Subscriptions tab calls the standalone lecture routes directly */
	standaloneLecture?: boolean;
	canToggle?: boolean;
	/** replaces the actions when progress can't be changed */
	lockedReason?: string;
	onClose: (changed: boolean) => void;
};

export const StudentUnitsModal: React.FC<Props> = ({
	student,
	courseId,
	standaloneLecture = false,
	canToggle = true,
	lockedReason,
	onClose,
}) => {
	const { displaySnackbar } = useSnackbar();
	const [units, setUnits] = useState<UnitRow[]>([]);
	const [header, setHeader] = useState<Header>({});
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState<string>();
	const changed = useRef(false);
	const base = standaloneLecture ? 'standalone-courses/lectures/progress/course' : 'progress/course';
	const scope = standaloneLecture ? { standalone_lecture_id: courseId } : {};

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const response = await api.post(`${base}/units`, { course_id: courseId, user_id: student.id, ...scope });
			setUnits(apiData<UnitRow[]>(response) || []);
			setHeader(response.data.meta || {});
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, 'Unable to load units'));
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [base, courseId, student.id, displaySnackbar]);

	useEffect(() => {
		load();
	}, [load]);

	const run = async (unit: UnitRow, action: Action) => {
		setBusy(`${unit.id}-${action}`);
		try {
			await api.post(`${base}/units/toggle`, { unit_id: unit.id, user_id: student.id, action, ...scope });
			changed.current = true;
			displaySnackbar('success', action === 'reset' ? 'Unit reset successfully' : 'Unit completed successfully');
			await load();
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, `Unable to ${action} the unit`));
		} finally {
			setBusy(undefined);
		}
	};

	const completed = units.filter((unit) => unit.status === 'completed').length;
	const studentName = [student.first_name, student.last_name].filter(Boolean).join(' ');

	return (
		<Modal open onClose={() => onClose(changed.current)}>
			<Container>
				<HeaderRow>
					<div>
						<StudentName>
							{studentName || '-'}
							{student.username && <Username>({student.username})</Username>}
						</StudentName>
						<Title>{header.course?.name ?? ''}</Title>
						{header.course?.parent && <Subtitle>{header.course.parent}</Subtitle>}
					</div>
					{!loading && units.length > 0 && (
						<Summary>
							<strong>{completed}</strong> / {units.length} units completed
						</Summary>
					)}
				</HeaderRow>
				{lockedReason && <Locked>{lockedReason}</Locked>}
				<List>
					{loading && !units.length ? (
						<Center>
							<CircularProgress size={28} />
						</Center>
					) : units.length === 0 ? (
						<Center>No units</Center>
					) : (
						units.map((unit) => {
							const status = (unit.status && STATUS[unit.status]) || NOT_STARTED;
							const type = UNIT_TYPES[Number(unit.type_id)];
							const done = unit.status === 'completed';
							const failedTest = unit.status === 'failed';
							return (
								<Row key={unit.id}>
									<Tooltip title={status.label} placement="top">
										<span>{status.icon}</span>
									</Tooltip>
									{type ? (
										<Tooltip title={type.label} placement="top">
											<type.Icon sx={{ color: done ? '#5ac0fc' : '#bdbdbd', fontSize: 22 }} />
										</Tooltip>
									) : (
										<span />
									)}
									<Tooltip title={unit.name} placement="top-start">
										<Name>{unit.name}</Name>
									</Tooltip>
									<Usage>{usage(unit)}</Usage>
									<Time>{Number(unit.time) > 0 ? formatDuration(Number(unit.time)) : '-'}</Time>
									<Actions>
										{canToggle &&
											!lockedReason &&
											actionsFor(unit.status).map((action) => (
												<Tooltip
													key={action}
													title={
														action === 'reset'
															? `Reset — ${type?.resets ?? 'clears its progress'}`
															: failedTest
															? 'Complete — pass the unit and keep the attempts'
															: 'Mark completed'
													}
												>
													<span>
														<IconButton size="small" disabled={busy !== undefined} onClick={() => run(unit, action)}>
															{busy === `${unit.id}-${action}` ? (
																<CircularProgress size={18} />
															) : action === 'reset' ? (
																<ReplayIcon fontSize="small" />
															) : (
																<CheckIcon fontSize="small" />
															)}
														</IconButton>
													</span>
												</Tooltip>
											))}
									</Actions>
								</Row>
							);
						})
					)}
				</List>
				<Footer>
					<Button color="warning" variant="text" onClick={() => onClose(changed.current)}>
						Close
					</Button>
				</Footer>
			</Container>
		</Modal>
	);
};

export default StudentUnitsModal;

const Container = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: min(50rem, calc(100vw - 2rem));
	border-radius: 10px;
	background-color: #ffffff;
	padding: 1.625rem 1.5rem 0.5rem;
`;
const HeaderRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 1rem;
	margin-bottom: 1.25rem;
`;
const StudentName = styled.p`
	color: #251b33;
	font-size: 1.25rem;
	font-weight: 600;
`;
const Username = styled.span`
	font-weight: 400;
	margin-inline-start: 0.4rem;
`;
const Title = styled.p`
	color: #251b33;
	font-size: 1rem;
	font-weight: 600;
	margin-top: 0.5rem;
`;
const Subtitle = styled.p`
	color: #6e7577;
	font-size: 0.875rem;
	margin-top: 0.15rem;
`;
const Summary = styled.p`
	color: #6e7577;
	font-size: 0.875rem;
	white-space: nowrap;
	strong {
		color: #251b33;
		font-size: 1.1rem;
	}
`;
const Locked = styled.p`
	color: #ff8532;
	font-size: 0.8rem;
	margin-bottom: 0.75rem;
`;
const List = styled.div`
	max-height: 24rem;
	overflow-y: auto;
`;
const Row = styled.div`
	display: grid;
	grid-template-columns: 30px 26px minmax(0, 1fr) 8rem 4rem 80px;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem 1rem;
	&:nth-child(even) {
		background: ${rgba('#707070', 0.1)};
	}
`;
const Name = styled.p`
	font-weight: 600;
	color: #251b33;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;
const Usage = styled.p`
	font-size: 0.8rem;
	color: #6e7577;
	text-align: end;
	white-space: nowrap;
`;
const Time = styled.p`
	font-size: 0.875rem;
	text-align: center;
`;
const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 0.25rem;
`;
const Center = styled.div`
	display: flex;
	justify-content: center;
	padding: 2rem;
	color: #6e7577;
`;
const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: 1rem;
`;
